import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ChefHat, Search, Users, ChevronRight, CalendarDays, FileDown, CheckCircle2 } from 'lucide-react';
import { moduleApi } from '../../../lib/api';
import { StatusFilterChips } from '../../ui/StatusFilterChips';
import { useAuth } from '../../../context/AuthContext';
import { resolveLogoUrl } from '../../../lib/resolveLogoUrl';
import { generateTechnicalSheetPdf } from '../../../lib/menusConvivesTechnicalSheetPdfGenerator';
import { generateClientRecapPdf } from '../../../lib/menusConvivesClientRecapPdfGenerator';

interface ServiceRow {
    event_id: string;
    label: string;
    date: string;
    status: string;
    guest_count: number;
    invited_count: number;
    responded_count: number;
    finalized_at?: string | null;
}

/** Le jour courant à Paris, en AAAA-MM-JJ — comparable directement aux dates renvoyées par le serveur. */
const jourParis = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' });

const fmtDate = (d?: string | null) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }); } catch { return '—'; }
};
const fmtHeure = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
};

/**
 * Écran d'accueil du module « Cuisine > Mode terrain » — la liste des services.
 *
 * Avant, le Mode terrain n'était atteignable que par un lien depuis la fiche d'un événement : un chef devait
 * traverser le module commercial un soir de service pour trouver son écran. Il a maintenant sa porte
 * d'entrée, et c'est aussi ce qui justifie qu'il soit un module à part (demande d'Emeni, 2026-08-26).
 *
 * « Ce soir » d'abord, et par défaut : c'est la seule question que se pose quelqu'un qui ouvre cet écran en
 * cuisine. Les autres onglets servent à préparer demain ou à revenir sur un service passé.
 *
 * La liste vient de l'inventaire déjà servi à Menus & Convives (`/evenementiel-convives-overview`) : c'est
 * la même donnée, et en tenir un second inventaire garantirait qu'ils divergent.
 */
export default function ModeTerrainListPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [rows, setRows] = React.useState<ServiceRow[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [search, setSearch] = React.useState('');
    const [onglet, setOnglet] = React.useState('aujourdhui');
    const [erreur, setErreur] = React.useState('');
    const [occupe, setOccupe] = React.useState('');

    React.useEffect(() => {
        moduleApi.getEvenementielConvivesOverview()
            .then((r: any) => setRows(Array.isArray(r) ? r : []))
            .catch((e: any) => setErreur(e?.message || 'Chargement impossible.'))
            .finally(() => setLoading(false));
    }, []);

    const aujourdhui = jourParis();
    const quand = (r: ServiceRow) => {
        const jour = String(r.date || '').slice(0, 10);
        if (jour === aujourdhui) return 'aujourdhui';
        return jour > aujourdhui ? 'avenir' : 'passes';
    };

    const options = [
        { value: 'aujourdhui', label: 'Ce soir', count: rows.filter((r) => quand(r) === 'aujourdhui').length },
        { value: 'avenir', label: 'À venir', count: rows.filter((r) => quand(r) === 'avenir').length },
        { value: 'passes', label: 'Passés', count: rows.filter((r) => quand(r) === 'passes').length },
    ];

    const filtrees = rows
        .filter((r) => onglet === 'all' || quand(r) === onglet)
        .filter((r) => !search.trim() || r.label.toLowerCase().includes(search.trim().toLowerCase()))
        // Les services passés se lisent du plus récent au plus ancien ; ceux à venir, du plus proche au plus
        // lointain. Dans les deux cas, ce qu'on cherche est en haut.
        .sort((a, b) => (onglet === 'passes' ? String(b.date).localeCompare(String(a.date)) : String(a.date).localeCompare(String(b.date))));

    // EXACTEMENT les mêmes documents que ceux servis par Menus & Convives, produits par les mêmes
    // générateurs : le chef n'a pas à traverser le module commercial pour les obtenir, mais il ne reçoit pas
    // pour autant une deuxième version des documents, qui finirait par diverger de la première.
    // Différence assumée : ils sont disponibles AVANT la clôture. Dans Menus & Convives ce sont des archives ;
    // ici c'est ce qu'on emporte en cuisine le soir même — attendre la clôture n'aurait aucun sens.
    const entete = (r: ServiceRow) => ({
        eventLabel: r.label || 'Privatisation',
        eventDate: r.date,
        issuerName: user?.company_name || undefined,
        logoUrl: resolveLogoUrl(user?.logoUrl || user?.logo_url) || null,
        brandColor: user?.theme_color || undefined,
    });

    const ficheCuisine = async (r: ServiceRow) => {
        setOccupe(r.event_id + ':cuisine'); setErreur('');
        try {
            const sheet: any = await moduleApi.getEvenementielCollecteTechnicalSheet(r.event_id);
            await generateTechnicalSheetPdf({
                ...entete(r),
                totalResponded: sheet?.total_responded || 0,
                dishes: Array.isArray(sheet?.dishes) ? sheet.dishes : [],
                restrictionsAllergies: Array.isArray(sheet?.restrictions_allergies) ? sheet.restrictions_allergies : [],
                includeAllergies: true,
            });
        } catch (e: any) { setErreur(e?.message || 'Erreur fiche cuisine.'); } finally { setOccupe(''); }
    };

    const recapConvives = async (r: ServiceRow) => {
        setOccupe(r.event_id + ':recap'); setErreur('');
        try {
            const recap: any = await moduleApi.getEvenementielCollecteClientRecap(r.event_id);
            await generateClientRecapPdf({
                ...entete(r),
                guests: Array.isArray(recap?.guests)
                    ? recap.guests.map((g: any) => ({ name: g.name, tableNumber: g.table_number, dishesByCategory: g.dishes_by_category || {}, allergies: g.allergies || [], restrictions: g.restrictions || [] }))
                    : [],
            });
        } catch (e: any) { setErreur(e?.message || 'Erreur récap convives.'); } finally { setOccupe(''); }
    };

    const boutonPdf = 'flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-surface border border-border text-[11px] font-bold text-content hover:border-brand-500/40 disabled:opacity-50';

    return (
        <div className="space-y-5">
            <div>
                <h1 className="text-xl font-bold text-content tracking-tight flex items-center gap-2">
                    <ChefHat size={18} /> Mode terrain
                </h1>
                <p className="text-content-muted text-sm mt-0.5">
                    Le service en cours, table par table : ce qui doit sortir, et les allergies à respecter.
                </p>
            </div>

            {erreur && <p className="text-xs text-red-500 font-semibold">{erreur}</p>}

            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un service..."
                        className="bg-surface border border-border rounded-lg pl-8 pr-3 py-1.5 text-xs text-content outline-none focus:border-brand-500 w-56" />
                </div>
                <StatusFilterChips options={options} value={onglet} onChange={setOnglet} />
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>
            ) : filtrees.length === 0 ? (
                <p className="text-sm text-content-muted py-10 text-center">
                    {onglet === 'aujourdhui'
                        ? "Aucun service aujourd'hui. Les prochains sont dans « À venir »."
                        : 'Aucun service ne correspond à cette recherche.'}
                </p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtrees.map((r) => {
                        const ceSoir = quand(r) === 'aujourdhui';
                        const complet = r.guest_count > 0 && r.responded_count >= r.guest_count;
                        return (
                            <div key={r.event_id}
                                className={`rounded-2xl border p-3 space-y-2 transition-colors ${ceSoir ? 'border-brand-500/50 bg-brand-500/5' : 'border-border bg-surface-soft'}`}>
                                {/* Zone cliquable séparée des boutons : une carte entièrement <button> ne peut
                                    pas contenir les téléchargements. Même découpe que Menus & Convives. */}
                                <div onClick={() => navigate(`/mode-terrain/${r.event_id}`)} className="cursor-pointer space-y-1.5">
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="text-sm font-black text-content min-w-0 truncate">{r.label || 'Sans nom'}</p>
                                        <ChevronRight size={15} className="text-content-muted shrink-0 mt-0.5" />
                                    </div>
                                    <p className="text-[11px] text-content-muted flex items-center gap-1.5">
                                        <CalendarDays size={11} />
                                        {ceSoir ? <span className="font-bold text-brand-600 dark:text-brand-400">Aujourd'hui</span> : fmtDate(r.date)}
                                        {fmtHeure(r.date) && <span>· {fmtHeure(r.date)}</span>}
                                    </p>
                                    {/* LE CHIFFRE QUI COMMANDE LA SOIRÉE. « 234/350 ont répondu » se lit d'un coup
                                        d'œil, là où « 234 réponses reçues » oblige à retrouver l'effectif ailleurs
                                        pour savoir si la collecte est finie. Demande d'Emeni (2026-08-26). */}
                                    <p className={`text-[11px] flex items-center gap-1.5 font-bold ${complet ? 'text-green-600 dark:text-green-400' : 'text-content-muted'}`}>
                                        {complet ? <CheckCircle2 size={11} /> : <Users size={11} />}
                                        {r.guest_count > 0
                                            ? <>{r.responded_count}/{r.guest_count} ont répondu</>
                                            : <>Aucun convive importé</>}
                                    </p>
                                </div>

                                {/* Les documents de la soirée, à portée de main en cuisine. Sans convive importé
                                    ils seraient vides : on ne les propose pas plutôt que de livrer un PDF blanc. */}
                                {r.guest_count > 0 && (
                                    <div className="flex gap-1.5 pt-1.5 border-t border-border">
                                        <button type="button" onClick={() => ficheCuisine(r)} disabled={occupe === r.event_id + ':cuisine'} className={boutonPdf}>
                                            {occupe === r.event_id + ':cuisine' ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />} Fiche cuisine
                                        </button>
                                        <button type="button" onClick={() => recapConvives(r)} disabled={occupe === r.event_id + ':recap'} className={boutonPdf}>
                                            {occupe === r.event_id + ':recap' ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />} Récap convives
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
